// use-update-team-full.ts — Teams suite, action
//
// PUT /api/teams/:teamId — the one team-update route, atomic across metadata and
// members. Members with `id` are updated, members without are created, existing
// members not in the payload are deleted. Omitting `members` entirely leaves the
// roster untouched, so a metadata-only edit is this same hook with no `members`.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UpdateTeamFullResponse, UpdateTeamRequest } from '@cabal/shared/domain/session/team/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export interface UpdateTeamFullVars {
  teamId: string;
  data: UpdateTeamRequest;
}

export function useUpdateTeamFull(): ActionResult<UpdateTeamFullVars, UpdateTeamFullResponse> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<UpdateTeamFullVars, UpdateTeamFullResponse>(
    useMemo(
      () => ({
        mutationFn: ({ teamId, data }) => api.teams.byId(teamId).update({ body: data }),
        onInvalidate: (_data, { teamId }) => {
          queryClient.invalidateQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
